import { useEffect, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useCreateMonitorBlock } from '@/hooks/use-create-monitor-block'
import { apiV2 } from '@/lib/api-v2-client'
import { CREATE_BLOCK_REASON, FREE_DOMAIN_RULE, PAID_ANY_EMAIL, looksLikeFreeMail } from '@/lib/free-domain'
import { canManageBilling } from '@/lib/subscription-access'
import { cn } from '@/lib/utils'

const BANNER_ID = 'create-monitor-block'
const OUTLINE = 'border-status-degraded bg-transparent text-status-degraded hover:bg-status-degraded-soft'

/**
 * Shown on every dashboard page while the server would refuse a new monitor (OpenSpec 141).
 * It stays until the problem is solved, so the user learns about it before any form, not after.
 */
export function CreateMonitorBlockBanner() {
  const { user, refreshAuth } = useAuth()
  const block = useCreateMonitorBlock()
  const blocked = block !== null

  // The confirm link opens in another tab. Ask again on return, or the button stays disabled.
  useEffect(() => {
    if (!blocked) return
    const recheck = () => void refreshAuth().catch(() => {})
    window.addEventListener('focus', recheck)
    return () => window.removeEventListener('focus', recheck)
  }, [blocked, refreshAuth])

  const { data: emails } = useQuery({
    queryKey: ['account-emails'],
    queryFn: () => apiV2.listAccountEmails(),
    enabled: block === 'work_email',
  })
  if (!block) return null

  // A work email was added and waits for its link: do not ask for it again.
  // This banner is on every page, so a surprise in this answer must not take the page down.
  const waiting = emails?.emails?.find((e) => !e.confirmed && !looksLikeFreeMail(e.email))?.email

  return (
    <div
      id={BANNER_ID}
      role="status"
      tabIndex={-1}
      className="flex flex-wrap items-center justify-between gap-3 border-b border-status-degraded bg-status-degraded-soft px-4 py-3 text-sm outline-none"
    >
      <div className="flex min-w-0 items-start gap-2 text-status-degraded">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        {block === 'confirm_email' ? (
          <p>
            <span className="font-semibold">Confirm your email address to add monitors on the free plan.</span>{' '}
            Sign in with an email code or link, Google or GitHub to confirm it.
          </p>
        ) : waiting ? (
          <p>
            <span className="font-semibold">Open the link we sent to <span className="break-all">{waiting}</span> to create monitors.</span>{' '}
            This page updates when you come back.
          </p>
        ) : (
          <p>
            <span className="font-semibold">Add a work email to create monitors.</span>{' '}
            {FREE_DOMAIN_RULE} {PAID_ANY_EMAIL}
            {!canManageBilling(user) && ' Your owner or admin can move this account to Pro.'}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {block === 'confirm_email' ? (
          <Button asChild size="sm">
            <Link to="/login">Open the sign-in page</Link>
          </Button>
        ) : (
          <Button asChild size="sm">
            <Link to="/dashboard/settings" search={{ tab: 'profile', billing: undefined, plan: undefined }} hash="emails">
              {waiting ? 'Open email settings' : 'Add a work email'}
            </Link>
          </Button>
        )}
        {canManageBilling(user) && (
          <Button asChild size="sm" variant="outline" className={OUTLINE}>
            <Link to="/pricing" hash="plan-pro">See Pro, from $3/mo</Link>
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * Looks disabled, but stays in the tab order so a screen reader can reach it and read why
 * (`disabled` would hide it). A click moves focus to the banner, where the actions are.
 */
export function BlockedActionButton({ children, id, className }: { children: ReactNode; id?: string; className?: string }) {
  return (
    <Button
      id={id}
      type="button"
      aria-disabled="true"
      aria-describedby={BANNER_ID}
      className={cn('cursor-not-allowed opacity-50 hover:bg-cta', className)}
      onClick={() => document.getElementById(BANNER_ID)?.focus()}
    >
      {children}
    </Button>
  )
}

/** The short reason that sits next to a disabled create button. */
export function CreateBlockReason() {
  const block = useCreateMonitorBlock()
  if (!block) return null
  return <p className="text-sm text-body" data-testid="create-block-reason">{CREATE_BLOCK_REASON[block]}</p>
}

/** A link to the new-monitor form, or a disabled button while the server would refuse a monitor. */
export function CreateMonitorButton({
  children,
  type,
  id,
  className,
}: {
  children: ReactNode
  type?: 'browser' | 'http'
  id?: string
  className?: string
}) {
  const block = useCreateMonitorBlock()
  if (block) return <BlockedActionButton id={id} className={className}>{children}</BlockedActionButton>
  return (
    <Button id={id} asChild className={className}>
      <Link to="/dashboard/monitors/new" search={type ? { type } : undefined}>{children}</Link>
    </Button>
  )
}
